import { PeriodPreset } from "@utils/reportingPeriod";
import { DashboardScope } from "catchment-common/interfaces/dashboardSurface";
import * as React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState
} from "react";

/** One period per surface, so the org lens and the project lens stay
 * independent — neither propagates into the other. */
export type PeriodBySurface = Record<DashboardScope, PeriodPreset>;

/** How the Project Scope filter spells "no project chosen". */
export const ALL_PROJECTS_LABEL = "All Projects";

interface AnalyticsFilterContextType {
  periodBySurface: PeriodBySurface;
  setPeriod: (scope: DashboardScope, preset: PeriodPreset) => void;
  /** Project Scope — org surface only. `[]` means every accessible project,
   * which is the shape both `useOrgData` and the backend already speak, so
   * multi-select is additive later. */
  projectIds: string[];
  /** The filter drawer passes the option's label alongside its id. Exports stamp the
   * scope into their header, and the name is only ever known to whoever picked
   * it — resolving an id back to a name anywhere else would mean refetching the
   * project list on surfaces that have no other use for it. */
  setProjectIds: (projectIds: string[], projectScopeLabel?: string) => void;
  /** The filter's current wording, so a stamped export reads exactly as the
   * drawer does. `All Projects` whenever `projectIds` is empty. */
  projectScopeLabel: string;
}

/** All time is the default, so day one is numerically identical to today. */
const DEFAULT_PERIODS: PeriodBySurface = {
  [DashboardScope.Organisation]: PeriodPreset.AllTime,
  [DashboardScope.Project]: PeriodPreset.AllTime
};

const AnalyticsFilterContext = createContext<AnalyticsFilterContextType>({
  periodBySurface: DEFAULT_PERIODS,
  projectIds: [],
  projectScopeLabel: ALL_PROJECTS_LABEL,
  setPeriod: () => {},
  setProjectIds: () => {}
});

/**
 * Session-scoped analytics filter state — no URL params, no localStorage.
 *
 * Mounted in `RootShell`, the only component above the route table: layouts are
 * rendered inside each route element and therefore remount on navigation, so a
 * provider in a layout would drop the selection every time the user moved
 * between sidebar sections.
 */
export const AnalyticsFilterProvider = ({
  children
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const [periodBySurface, setPeriodBySurface] =
    useState<PeriodBySurface>(DEFAULT_PERIODS);
  // Ids and label move together — held apart they could tear, and a header that
  // says one project while the tabs requested another is the bug this exists to
  // prevent.
  const [projectScope, setProjectScope] = useState<{
    projectIds: string[];
    projectScopeLabel: string;
  }>({ projectIds: [], projectScopeLabel: ALL_PROJECTS_LABEL });

  const setPeriod = useCallback(
    (scope: DashboardScope, preset: PeriodPreset) =>
      setPeriodBySurface((current) => ({ ...current, [scope]: preset })),
    []
  );

  const setProjectIds = useCallback(
    (projectIds: string[], projectScopeLabel?: string) =>
      setProjectScope({
        projectIds,
        // No selection is always "All Projects"; an unlabelled selection falls
        // back to its id rather than to "All Projects", which would read as the
        // opposite of what was requested.
        projectScopeLabel:
          projectIds.length === 0
            ? ALL_PROJECTS_LABEL
            : (projectScopeLabel ?? projectIds[0])
      }),
    []
  );

  const value = useMemo(
    () => ({ ...projectScope, periodBySurface, setPeriod, setProjectIds }),
    [periodBySurface, projectScope, setPeriod, setProjectIds]
  );

  return (
    <AnalyticsFilterContext.Provider value={value}>
      {children}
    </AnalyticsFilterContext.Provider>
  );
};

export const useAnalyticsFilterContext = () =>
  useContext(AnalyticsFilterContext);
